import BugReportIcon from "@mui/icons-material/BugReport";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SpeedIcon from "@mui/icons-material/Speed";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { endOfDay, startOfDay, subDays } from "date-fns";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAX_DASHBOARD_VEHICLES,
  SourceStatus,
  useFleetDashboardData,
} from "./hooks/useFleetDashboardData";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { KpiTile } from "@/components/layout/KpiTile";
import { ResultsCapError } from "@/utilities/error/ResultsCapError";

/** A data source a section depends on, with the error text the section shows when it fails. */
interface SectionSource {
  status: SourceStatus;
  errorText: string;
  /** A full page is deterministic: shown as a warning without retry. */
  isCapped?: boolean;
}

function SourceError({ source }: { source: SectionSource }) {
  const { t } = useTranslation();
  if (source.isCapped) return <Alert severity="warning">{source.errorText}</Alert>;
  return (
    <Alert
      severity="error"
      action={
        <Button
          color="inherit"
          size="small"
          disabled={source.status.isRetrying}
          startIcon={source.status.isRetrying && <CircularProgress size={14} color="inherit" />}
          onClick={source.status.retry}
        >
          {t("fleetDashboard.retry")}
        </Button>
      }
    >
      {source.errorText}
    </Alert>
  );
}

function SectionPaper({
  title,
  sources,
  children,
}: {
  title: string;
  sources: SectionSource[];
  children: ReactNode;
}) {
  const failed = sources.find(source => source.status.isError);
  const isLoading = sources.some(source => source.status.isLoading);
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 280 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {failed ? (
        <SourceError source={failed} />
      ) : isLoading ? (
        <Stack gap={0.75}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Stack>
      ) : (
        children
      )}
    </Paper>
  );
}

/** Tile value: a placeholder while its source loads, a dash when it failed. */
function tileValue(status: SourceStatus, value: () => string): ReactNode {
  if (status.isLoading) return <Skeleton width={64} />;
  if (status.isError) return "—";
  return value();
}

export default function FleetDashboardPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(700));
  const [fromDate, setFromDate] = useState<Date>(() => startOfDay(subDays(new Date(), 6)));
  const [toDate, setToDate] = useState<Date>(() => endOfDay(new Date()));

  const data = useFleetDashboardData(fromDate, toDate);
  const { devicesStatus, vehiclesStatus, faultsStatus, fuelCellStatus } = data;
  const vehicles: SectionSource = {
    status: vehiclesStatus,
    errorText: t("fleetDashboard.vehiclesError", { count: vehiclesStatus.failedCount }),
  };
  const faults: SectionSource = {
    status: faultsStatus,
    ...(faultsStatus.error instanceof ResultsCapError
      ? { errorText: faultsStatus.error.i18nMessage(t), isCapped: true }
      : { errorText: t("fleetDashboard.faultsError") }),
  };
  const fuelCell: SectionSource = {
    status: fuelCellStatus,
    errorText: t("fleetDashboard.fuelCellError"),
  };

  return (
    <FilterStackLayout
      filterMaxWidth={1512}
      isMobile={isMobile}
      filters={
        <DateRangeSelect
          toDate={toDate}
          fromDate={fromDate}
          onChange={(nextFromDate, nextToDate) => {
            setFromDate(startOfDay(nextFromDate ?? new Date()));
            setToDate(endOfDay(nextToDate ?? nextFromDate ?? new Date()));
          }}
        />
      }
      content={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: 2,
            gap: 2,
          }}
        >
          {devicesStatus.isLoading ? (
            <Box sx={{ display: "grid", placeItems: "center", width: "100%", height: "100%" }}>
              <CircularProgress size={28} />
            </Box>
          ) : data.isOverVehicleCap ? (
            <Alert severity="info">
              {t(
                "fleetDashboard.tooManyVehicles",
                "Das Dashboard zeigt Kennzahlen für höchstens {{max}} Fahrzeuge, die aktuelle Auswahl enthält {{count}}. Bitte den Gruppenfilter eingrenzen.",
                { max: MAX_DASHBOARD_VEHICLES, count: data.vehicleCount },
              )}
            </Alert>
          ) : devicesStatus.isError ? (
            <SourceError
              source={{ status: devicesStatus, errorText: t("fleetDashboard.loadingError") }}
            />
          ) : (
            <>
              {data.isDeviceListTruncated && (
                <Alert severity="warning">
                  {t(
                    "fleetDashboard.deviceListTruncated",
                    "Die Fahrzeugliste ist zu groß und wurde gekürzt. Die Kennzahlen sind unvollständig.",
                  )}
                </Alert>
              )}
              {(vehiclesStatus.isLoading || vehiclesStatus.isRetrying) && (
                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      vehiclesStatus.totalCount > 0
                        ? (vehiclesStatus.loadedCount / vehiclesStatus.totalCount) * 100
                        : 0
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t("fleetDashboard.progress", {
                      loaded: vehiclesStatus.loadedCount,
                      total: vehiclesStatus.totalCount,
                    })}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" gap={1.5} flexWrap="wrap">
                <KpiTile
                  minWidth={200}
                  label={t("fleetDashboard.kpi.totalDevices")}
                  value={String(data.totalDevices)}
                  // Counted from the trips: partial while vehicles load or after some failed.
                  hint={
                    vehiclesStatus.isLoading || vehiclesStatus.isError
                      ? "—"
                      : t("fleetDashboard.kpi.totalDevicesHint", { count: data.activeDevices })
                  }
                  icon={<DirectionsCarIcon />}
                  color={theme.palette.primary.main}
                />
                <KpiTile
                  minWidth={200}
                  label={t("fleetDashboard.kpi.utilization")}
                  value={tileValue(vehiclesStatus, () =>
                    data.utilizationPercent != null
                      ? `${data.utilizationPercent.toFixed(0)} %`
                      : "—",
                  )}
                  hint={t(
                    "fleetDashboard.kpi.utilizationHint",
                    "Fahrzeuge mit Fahrten im Zeitraum",
                  )}
                  icon={<DirectionsCarIcon />}
                  color={theme.palette.success.main}
                />
                <KpiTile
                  minWidth={200}
                  label={t("fleetDashboard.kpi.maxSpeed")}
                  value={tileValue(vehiclesStatus, () =>
                    data.fleetMaxSpeedKmh != null ? `${data.fleetMaxSpeedKmh.toFixed(0)} km/h` : "—",
                  )}
                  hint={t("fleetDashboard.kpi.maxSpeedHint")}
                  icon={<SpeedIcon />}
                  color={theme.palette.warning.main}
                />
                <KpiTile
                  minWidth={200}
                  label={t("fleetDashboard.kpi.openFaults")}
                  value={tileValue(faultsStatus, () => String(data.openFaultCount))}
                  hint={t("fleetDashboard.kpi.openFaultsHint")}
                  icon={<BugReportIcon />}
                  color={theme.palette.error.main}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} gap={2} flexWrap="wrap">
                <SectionPaper
                  title={t("fleetDashboard.sections.fuel")}
                  sources={[vehicles, fuelCell]}
                >
                  <Stack gap={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        {t("fleetDashboard.fuel.combustion")}
                      </Typography>
                      <Typography variant="body2">
                        <strong>
                          {t("fleetDashboard.fuel.liters", {
                            value: data.combustionFuelUsedLiters.toFixed(1),
                          })}
                        </strong>{" "}
                        (
                        {t("fleetDashboard.fuel.vehicleCount", {
                          count: data.combustionVehicleCount,
                        })}
                        )
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        {t("fleetDashboard.fuel.fuelCell")}
                      </Typography>
                      <Typography variant="body2">
                        {t("fleetDashboard.fuel.vehicleCount", {
                          count: data.fuelCellVehicleCount,
                        })}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "fleetDashboard.fuel.fuelCellHint",
                        "Detaillierte H2-Verbrauchswerte siehe H2-Monitor",
                      )}
                    </Typography>
                  </Stack>
                </SectionPaper>

                <SectionPaper
                  title={t("fleetDashboard.sections.topSpeeds")}
                  sources={[vehicles]}
                >
                  {data.topSpeeds.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("fleetDashboard.noData")}
                    </Typography>
                  ) : (
                    <Stack gap={0.75}>
                      {data.topSpeeds.map(entry => (
                        <Stack key={entry.deviceId} direction="row" justifyContent="space-between">
                          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                            {entry.deviceName}
                          </Typography>
                          <Typography variant="body2">
                            <strong>
                              {t("fleetDashboard.speedValue", {
                                value: entry.maxSpeedKmh.toFixed(0),
                              })}
                            </strong>
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </SectionPaper>

                <SectionPaper
                  title={t("fleetDashboard.sections.topFaults")}
                  sources={[faults]}
                >
                  {data.topFaults.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("fleetDashboard.noData")}
                    </Typography>
                  ) : (
                    <Stack gap={0.75}>
                      {data.topFaults.map(entry => (
                        <Stack
                          key={entry.diagnosticId}
                          direction="row"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                            {entry.diagnosticName}
                          </Typography>
                          <Typography variant="body2">
                            <strong>{entry.count}</strong>
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </SectionPaper>
              </Stack>
            </>
          )}
        </Box>
      }
    />
  );
}
